import * as React from 'react';

import type {
  RelanceJournalEntryDto,
  RelanceTypeDto,
} from '@/api/relance-journal-client';
import { useRelanceJournal } from '@/api/use-relance-journal';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { StatusBadge, type StatusTone } from '@/components/ui/status-badge';

// =============================================================================
// campaign-relance-journal-screen.tsx — Écran ADMIN « Journal des relances » (R15-COM-05, DBU §2.7.1).
// =============================================================================
// Câble le read-model DÉJÀ exposé par l'API (GET /admin/campaigns/:campaignId/relances, AdminSessionGuard)
// à un écran de consultation admin. AUCUNE règle métier ici : l'agrégation multi-fournisseurs, la résolution
// de l'auteur et le grain (un envoi par contact) sont l'AUTORITÉ de l'API ; l'UI LIT et habille.
//
// SÉCURITÉ (SECOPS) : le journal AGRÈGE des relances de PLUSIEURS établissements. Chaque LIGNE est autonome
// (son propre supplierSiret + son propre destinataire) : JAMAIS un fournisseur n'est rendu comme destinataire
// d'un autre. Les données ne portent AUCUN nom nominatif de contact — le SIRET est l'identité présentée de
// l'établissement destinataire, et le contactId reste un identifiant LOGIQUE (jamais transformé en nom).
//
// WCAG 2.1 AA (ST-NFR-03) :
//   - <table> avec <caption> + <th scope="col"> ; type de relance en LIBELLÉ TEXTE (StatusBadge), la
//     couleur du tone n'est qu'un renfort (jamais seule porteuse d'information) ;
//   - filtres/tri en contrôles natifs étiquetés (<label htmlFor>) ;
//   - états de chargement/erreur/vide en role="status" / role="alert".

/** Expéditeur système FIXE : les données ne portent pas d'expéditeur par ligne (envoi via la plateforme). */
const SYSTEM_SENDER_LABEL = 'JEKO Référencement';

/** Libellé de l'auteur d'une relance AUTOMATIQUE (emisParAdmin === null) : aucun acteur humain. */
const SYSTEM_AUTHOR_LABEL = 'Automatique (système)';

/** Métadonnées d'affichage d'un type de relance : LIBELLÉ FR (porteur d'information) + tone DS (renfort). */
interface RelanceTypeMeta {
  readonly label: string;
  readonly tone: StatusTone;
}

/** Mapping type → { libellé, tone }. Le libellé reste l'unique porteur d'information (WCAG 1.4.1). */
function relanceTypeMeta(type: RelanceTypeDto): RelanceTypeMeta {
  switch (type) {
    case 'Individuelle':
      return { label: 'Individuelle', tone: 'invited' };
    case 'Groupee':
      return { label: 'Groupée', tone: 'active' };
    case 'Automatique':
      return { label: 'Automatique', tone: 'inactive' };
  }
}

/** Date d'envoi ISO 8601 → « JJ/MM/AAAA à HH:MM » (déterministe, lecture depuis les composants ISO). */
function formatSentAt(iso: string): string {
  const [datePart, timePart] = iso.split('T');
  const parts = (datePart ?? '').split('-');
  if (parts.length !== 3) {
    return iso;
  }
  const dmy = `${parts[2]}/${parts[1]}/${parts[0]}`;
  const hhmm = (timePart ?? '').slice(0, 5);
  return /^\d{2}:\d{2}$/.test(hhmm) ? `${dmy} à ${hhmm}` : dmy;
}

/** Valeur du filtre de type : « all » (défaut) ou un type précis. */
type TypeFilter = 'all' | RelanceTypeDto;
/** Direction de tri par date. « desc » (plus récentes d'abord) = défaut. */
type SortDir = 'asc' | 'desc';

export interface CampaignRelanceJournalScreenProps {
  readonly campaignId: string;
}

export function CampaignRelanceJournalScreen({
  campaignId,
}: CampaignRelanceJournalScreenProps): React.JSX.Element {
  const query = useRelanceJournal(campaignId);
  const [typeFilter, setTypeFilter] = React.useState<TypeFilter>('all');
  const [sortDir, setSortDir] = React.useState<SortDir>('desc');

  const entries = React.useMemo<readonly RelanceJournalEntryDto[]>(
    () => query.data?.entries ?? [],
    [query.data],
  );

  // Filtre (par type) + tri (par date). DBU §2.7.1 l.1526. Les chaînes ISO 8601 se comparent
  // lexicographiquement dans l'ordre chronologique (même format/timezone) — tri déterministe.
  const visibleEntries = React.useMemo<readonly RelanceJournalEntryDto[]>(() => {
    const filtered = entries.filter(
      (e) => typeFilter === 'all' || e.type === typeFilter,
    );
    const sorted = [...filtered].sort((a, b) =>
      a.sentAt < b.sentAt ? -1 : a.sentAt > b.sentAt ? 1 : 0,
    );
    return sortDir === 'desc' ? sorted.reverse() : sorted;
  }, [entries, typeFilter, sortDir]);

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Journal des relances
          </h1>
          <p className="text-sm text-muted-foreground">
            Campagne <span className="font-mono">{campaignId}</span> — historique
            des relances émises (individuelles, groupées, automatiques), tous
            établissements confondus.
          </p>
        </div>
      </header>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="space-y-1">
              <CardTitle>Relances émises</CardTitle>
              <CardDescription>
                Chaque ligne correspond à un envoi vers un établissement
                destinataire (identifié par son SIRET). Les envois sont
                individualisés : aucun destinataire n’est visible des autres.
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-2">
                <Label htmlFor="relance-type-filter">Filtrer par type</Label>
                <select
                  id="relance-type-filter"
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value as TypeFilter);
                  }}
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <option value="all">Tous les types</option>
                  <option value="Individuelle">Individuelle</option>
                  <option value="Groupee">Groupée</option>
                  <option value="Automatique">Automatique</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="relance-sort-dir">Trier par date</Label>
                <select
                  id="relance-sort-dir"
                  value={sortDir}
                  onChange={(e) => {
                    setSortDir(e.target.value as SortDir);
                  }}
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <option value="desc">Plus récentes d’abord</option>
                  <option value="asc">Plus anciennes d’abord</option>
                </select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {query.isPending ? (
            <p role="status" className="text-muted-foreground">
              Chargement du journal des relances…
            </p>
          ) : query.isError ? (
            <p role="alert" className="text-destructive">
              Erreur de chargement : {query.error.message}
            </p>
          ) : entries.length === 0 ? (
            <p role="status" className="text-muted-foreground">
              Aucune relance émise pour cette campagne.
            </p>
          ) : visibleEntries.length === 0 ? (
            <p role="status" className="text-muted-foreground">
              Aucune relance ne correspond au filtre sélectionné.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="ds-table w-full border-collapse text-sm">
                <caption className="sr-only">
                  Journal des relances émises : auteur, date d’envoi,
                  établissement destinataire, expéditeur, type et canal
                </caption>
                <thead>
                  <tr className="border-b border-border text-left">
                    <th scope="col" className="py-2 pr-4 font-medium">
                      Auteur
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      Date
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      Destinataire
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      Expéditeur
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      Type
                    </th>
                    <th scope="col" className="py-2 font-medium">
                      Canal
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleEntries.map((entry) => {
                    const meta = relanceTypeMeta(entry.type);
                    return (
                      <tr
                        key={entry.relanceId}
                        className="border-b border-border align-top last:border-0"
                      >
                        <td className="py-2 pr-4">
                          {entry.emisParAdmin === null ? (
                            <span className="text-muted-foreground">
                              {SYSTEM_AUTHOR_LABEL}
                            </span>
                          ) : (
                            entry.emisParAdmin
                          )}
                        </td>
                        <td className="py-2 pr-4 tabular-nums">
                          {formatSentAt(entry.sentAt)}
                        </td>
                        <td className="py-2 pr-4">
                          <span className="block font-mono text-xs">
                            {entry.supplierSiret}
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            Établissement destinataire
                          </span>
                        </td>
                        <td className="py-2 pr-4">{SYSTEM_SENDER_LABEL}</td>
                        <td className="py-2 pr-4">
                          <StatusBadge tone={meta.tone}>
                            {meta.label}
                          </StatusBadge>
                        </td>
                        <td className="py-2">{entry.canal}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
